import { resolveBaseUrl } from "@coworker/http-client";
import { ServerSentEvents } from "./ServerSentEvents";
import { Notification } from "../types/notification.types";
import { notificationMessagesCache } from "./NotificationMessagesCache";

function getSseBaseUrl(): string {
  return `${resolveBaseUrl("notifications")}/notifications/sse`;
}

export class NotificationMessages {
  private sseEvents: ServerSentEvents | null = null;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private isClosedByConsumer = false;

  constructor(
    private storeId: string,
    private uid: string,
    private bearerToken: () => Promise<string | null>
  ) {}

  // Get createdAt from newest notification message.
  private getNewestCreatedAt() {
    const cachedMessages = notificationMessagesCache.getMessagesForStoreAndUid(
      this.storeId,
      this.uid
    );
    if (cachedMessages && cachedMessages.length) {
      const firstMessage = cachedMessages.at(0);
      return firstMessage!.createdAt;
    }
    return "";
  }

  // Open server sent event connection
  async open() {
    this.isClosedByConsumer = false;
    try {
      const headers = async (): Promise<HeadersInit> => ({
        Authorization: `Bearer ${(await this.bearerToken()) ?? ""}`,
        "Content-Type": "text/event-stream",
      });
      const url = `${getSseBaseUrl()}/stores/${encodeURIComponent(this.storeId)}/users/${encodeURIComponent(this.uid)}/messages`;
      this.sseEvents = new ServerSentEvents(url, headers, [
        `createdAt=${encodeURIComponent(this.getNewestCreatedAt())}`,
      ]);
      this.sseEvents.onmessage = ({ event, data }) => {
        try {
          if (event && event.includes("max-notifications-count:")) {
            const maxCount = parseInt((event.split(":")[1] ?? "").trim(), 10);
            if (!isNaN(maxCount)) {
              notificationMessagesCache.setMaxCount(
                this.storeId,
                this.uid,
                maxCount
              );
            }
          }

          if (event && event.includes("session-timeout") && data) {
            const json = JSON.parse(data) as { timeout?: unknown };
            const timeout = Number(json.timeout);
            if (Number.isFinite(timeout) && timeout > 0) {
              this.setReconnectTimer(timeout);
            }
            return;
          }

          if (data) {
            const messages = JSON.parse(data) as Notification[];
            notificationMessagesCache.update(this.storeId, this.uid, messages);
            this.sseEvents!.setQueryParams([
              `createdAt=${encodeURIComponent(this.getNewestCreatedAt())}`,
            ]);
          }
        } catch (error) {
          console.error(
            `NotificationMessages: onmessage failed ${(error as Error).message}`
          );
        }
      };
    } catch (error) {
      console.error(
        `NotificationMessages: open failed ${(error as Error).message}`
      );
    }
  }

  private setReconnectTimer(timeout: number) {
    this.clearReconnectTimer();
    const reconnectDelay =
      timeout > 30000 ? timeout - 30000 : Math.floor(timeout * 0.9);
    this.reconnectTimer = setTimeout(async () => {
      if (this.isClosedByConsumer) {
        return;
      }
      this.closeConnection(false);
      if (!this.isClosedByConsumer) {
        await this.open();
      }
    }, reconnectDelay);
  }

  private clearReconnectTimer() {
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
  }

  private closeConnection(markAsClosedByConsumer: boolean) {
    if (markAsClosedByConsumer) {
      this.isClosedByConsumer = true;
    }
    this.clearReconnectTimer();
    if (this.sseEvents) {
      this.sseEvents.close();
      this.sseEvents = null;
    }
  }

  // Close connection
  close() {
    this.closeConnection(true);
  }
}
